/**
 * Small utility primitives used across the Financial v2 components.
 */
import { ChevronDown, Lock } from "lucide-react";
import { useState } from "react";
import { useEntitlements } from "@/context/EntitlementsContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Link } from "react-router-dom";

/**
 * A collapsible section with a title, subtitle, and an optional entitlement lock.
 * If `entitlementKey` is set and the user is missing that feature, we render a
 * locked state with a "Why upgrade" explainer instead of the children.
 */
export function Section({ title, subtitle, entitlementKey, upsellPitch, defaultOpen = true, children, testId, headerRight, tier }) {
  const [open, setOpen] = useState(defaultOpen);
  const { has } = useEntitlements();
  const locked = entitlementKey && !has(entitlementKey);
  return (
    <section className="bg-card border border-border rounded-2xl mb-4 overflow-hidden" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-muted/30 transition-colors"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-display text-lg font-bold tracking-tight">{title}</h3>
            {locked && <span className="text-[10px] uppercase font-mono tracking-widest px-2 py-0.5 rounded bg-primary/10 text-primary flex items-center gap-1"><Lock size={10} /> {tier || "Pro"}</span>}
          </div>
          {subtitle && <div className="text-xs text-muted-foreground mt-0.5">{subtitle}</div>}
        </div>
        <div className="flex items-center gap-2">
          {headerRight}
          <ChevronDown size={16} className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-border">
          {locked ? (
            <UpsellPanel entitlementKey={entitlementKey} pitch={upsellPitch} tier={tier} />
          ) : (
            children
          )}
        </div>
      )}
    </section>
  );
}

function UpsellPanel({ entitlementKey, pitch, tier }) {
  return (
    <div className="py-6 flex flex-col items-start gap-3">
      <div className="text-sm text-muted-foreground max-w-lg">{pitch || "Unlock this section to plan and reason across your entire financial picture."}</div>
      <Link to="/app/pricing" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm font-semibold" data-testid={`upsell-${entitlementKey}`}>
        Unlock with {tier || "Pro"}
      </Link>
    </div>
  );
}

/** A single labelled money/number input. */
export function NumberField({ label, value, onChange, prefix, suffix, testId, placeholder, step = "any" }) {
  return (
    <label className="block">
      <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</span>
      <div className="mt-1.5 flex items-center bg-background border border-border rounded-md focus-within:border-primary">
        {prefix && <span className="pl-3 text-xs text-muted-foreground">{prefix}</span>}
        <input
          type="number"
          step={step}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 bg-transparent outline-none text-sm"
          data-testid={testId}
        />
        {suffix && <span className="pr-3 text-xs text-muted-foreground">{suffix}</span>}
      </div>
    </label>
  );
}

export function TextField({ label, value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</span>
      <input
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1.5 w-full px-3 py-2 bg-background border border-border rounded-md focus:border-primary focus:outline-none text-sm"
        data-testid={testId}
      />
    </label>
  );
}

export function SelectField({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</span>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="mt-1.5 w-full px-3 py-2 bg-background border border-border rounded-md focus:border-primary focus:outline-none text-sm"
        data-testid={testId}
      >
        {options.map(([v, l]) => <option key={v || "_none"} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

/** Format a money amount using the plan's currency. */
export function Money({ amount, currency, className = "" }) {
  const { format } = useCurrency();
  const display = amount == null
    ? "—"
    : (currency ? format(amount, { currency }) : format(amount));
  return <span className={className}>{display}</span>;
}

/** Big colored stat cell. */
export function StatCell({ label, value, sub, tone = "default", testId }) {
  const toneCls = tone === "warn" ? "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))/0.06]" :
                  tone === "good" ? "border-emerald-500/40 bg-emerald-500/6" :
                  tone === "bad" ? "border-destructive/40 bg-destructive/6" : "border-border";
  return (
    <div className={`border rounded-xl p-3 ${toneCls}`} data-testid={testId}>
      <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-xl font-black mt-1 tabular-nums leading-tight">{value ?? "—"}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export function ProgressBar({ value, tone = "primary", testId }) {
  const cls = tone === "primary" ? "bg-primary" : tone === "good" ? "bg-emerald-500" : tone === "warn" ? "bg-amber-500" : "bg-primary";
  const pct = Math.max(0, Math.min(100, value || 0));
  return (
    <div className="w-full h-2 bg-muted rounded-full overflow-hidden" data-testid={testId}>
      <div className={`${cls} h-full transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
}
